import '@/components/agent-chat/messages/styles.css';

import {
  autoUpdate,
  offset,
  shift,
  useClick,
  useDismiss,
  useFloating,
  useInteractions,
} from '@floating-ui/react';
import {
  AlertCircle,
  CheckCircle2,
  ExternalLink,
  Link2,
  Loader2,
  RefreshCw,
  X,
} from 'lucide-react';
import * as React from 'react';
import { createPortal } from 'react-dom';

import { FirmLink } from '@/components/entity-links/entities/FirmLink';
import { MatterLink } from '@/components/entity-links/entities/MatterLink';
import { Badge } from '@/components/ui/catalyst/badge';
import { Button } from '@/components/ui/catalyst/button';
import { Textarea } from '@/components/ui/catalyst/textarea';
import { DatePicker } from '@/components/ui/date-picker';
import {
  Dialog,
  DialogTitle,
  RightDialogContent,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  enhancedApi as externalTaskApi,
  type ExternalTaskActionSchema,
  getPollingInterval,
} from '@/services/api/externalTaskService';

import { formatDate } from './dateUtils';
import { ExternalTaskMatchStatus } from './ExternalTaskMatchStatus';
import { LinkedTasksList } from './LinkedTasksList';
import { MarkdownViewer } from './MarkdownViewer';
import { TaskDetail } from './task-detail/TaskDetail';
import { TaskSection } from './task-detail/TaskSection';
import { TaskLinkingInterface } from './TaskLinkingInterface';

interface ExternalTaskDetailProps {
  externalTaskId: string;
  onClose: () => void;
}

/**
 * Component to display a pending action with status indicator
 */
const PendingActionItem: React.FC<{
  action: ExternalTaskActionSchema;
  onRetry: (actionId: string) => void;
  onDismiss: (actionId: string) => void;
  isRetrying: boolean;
  isDismissing: boolean;
}> = ({ action, onRetry, onDismiss, isRetrying, isDismissing }) => {
  const getActionLabel = () => {
    switch (action.action_type) {
      case 'ADD_COMMENT': {
        if (action.payload.type === 'ADD_COMMENT') {
          // Truncate long comments
          const { comment } = action.payload;
          const truncated =
            comment.length > 30 ? `${comment.slice(0, 30)}...` : comment;
          return `Adding comment: "${truncated}"`;
        }
        return 'Adding comment';
      }
      case 'CHANGE_STATUS': {
        const status =
          action.payload.type === 'CHANGE_STATUS'
            ? action.payload.status.toLowerCase()
            : '';
        return `Changing status to ${status}`;
      }
      case 'CHANGE_DUE_DATE': {
        if (action.payload.type === 'CHANGE_DUE_DATE') {
          const date = new Date(action.payload.due_date);
          const formatted = date.toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
          });
          return `Changing due date to ${formatted}`;
        }
        return 'Changing due date';
      }
      default:
        return 'Processing action';
    }
  };

  const getStatusIcon = () => {
    switch (action.status) {
      case 'PENDING':
      case 'IN_PROGRESS':
        return <Loader2 className="h-3.5 w-3.5 animate-spin text-blue-500" />;
      case 'COMPLETED':
        return <CheckCircle2 className="h-3.5 w-3.5 text-green-500" />;
      case 'FAILED':
        return <AlertCircle className="h-3.5 w-3.5 text-red-500" />;
      default:
        return null;
    }
  };

  const getStatusColor = () => {
    switch (action.status) {
      case 'PENDING':
      case 'IN_PROGRESS':
        return 'bg-blue-50 border-blue-200';
      case 'COMPLETED':
        return 'bg-green-50 border-green-200';
      case 'FAILED':
        return 'bg-red-50 border-red-200';
      default:
        return 'bg-slate-50 border-slate-200';
    }
  };

  return (
    <div
      className={`flex items-center justify-between px-3 py-2 rounded-md border ${getStatusColor()}`}
    >
      <div className="flex items-center gap-2">
        {getStatusIcon()}
        <span className="text-sm font-medium text-slate-700">
          {getActionLabel()}
        </span>
        {action.status === 'IN_PROGRESS' && (
          <span className="text-xs text-slate-500">syncing to CMS...</span>
        )}
        {action.status === 'FAILED' && action.error_message && (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="text-xs text-red-600 cursor-help truncate max-w-[200px]">
                  {action.error_message}
                </span>
              </TooltipTrigger>
              <TooltipContent className="max-w-sm">
                <p>{action.error_message}</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )}
      </div>
      {action.status === 'FAILED' && (
        <div className="flex items-center gap-1.5">
          <Button
            onClick={() => onRetry(action.id as string)}
            disabled={isRetrying || isDismissing}
            className="h-7 px-2 text-xs flex items-center justify-center gap-1"
            outline
          >
            {isRetrying ? (
              <Loader2 className="h-3 w-3 animate-spin" />
            ) : (
              <RefreshCw className="h-3 w-3" />
            )}
            <span>Retry</span>
          </Button>
          <Button
            onClick={() => onDismiss(action.id as string)}
            disabled={isRetrying || isDismissing}
            className="h-7 px-2 text-xs text-slate-500 hover:text-slate-700 flex items-center justify-center gap-1"
            outline
          >
            {isDismissing ? (
              <Loader2 className="h-3 w-3 animate-spin" />
            ) : (
              <X className="h-3 w-3" />
            )}
            <span>Dismiss</span>
          </Button>
        </div>
      )}
    </div>
  );
};

/**
 * Component to display a pending comment inline in the comments list
 */
const PendingCommentItem: React.FC<{
  action: ExternalTaskActionSchema;
  onRetry: (actionId: string) => void;
  onDismiss: (actionId: string) => void;
  isRetrying: boolean;
  isDismissing: boolean;
}> = ({ action, onRetry, onDismiss, isRetrying, isDismissing }) => {
  // Extract comment text from payload
  const commentText =
    action.payload.type === 'ADD_COMMENT' ? action.payload.comment : '';

  const getStatusIndicator = () => {
    switch (action.status) {
      case 'PENDING':
      case 'IN_PROGRESS':
        return (
          <div className="flex items-center gap-1.5 text-blue-600">
            <Loader2 className="h-3 w-3 animate-spin" />
            <span className="text-xs">
              {action.status === 'IN_PROGRESS' ? 'Syncing...' : 'Pending...'}
            </span>
          </div>
        );
      case 'FAILED':
        return (
          <div className="flex items-center gap-1.5">
            <AlertCircle className="h-3 w-3 text-red-500" />
            <span className="text-xs text-red-600">Failed</span>
            <Button
              onClick={() => onRetry(action.id as string)}
              disabled={isRetrying || isDismissing}
              className="h-5 px-1.5 text-xs ml-1"
              outline
            >
              {isRetrying ? (
                <Loader2 className="h-2.5 w-2.5 animate-spin" />
              ) : (
                <RefreshCw className="h-2.5 w-2.5" />
              )}
            </Button>
            <Button
              onClick={() => onDismiss(action.id as string)}
              disabled={isRetrying || isDismissing}
              className="h-5 px-1.5 text-xs text-slate-500 hover:text-slate-700"
              outline
            >
              {isDismissing ? (
                <Loader2 className="h-2.5 w-2.5 animate-spin" />
              ) : (
                <X className="h-2.5 w-2.5" />
              )}
            </Button>
          </div>
        );
      case 'COMPLETED':
        return (
          <div className="flex items-center gap-1.5 text-green-600">
            <CheckCircle2 className="h-3 w-3" />
            <span className="text-xs">Synced</span>
          </div>
        );
      default:
        return null;
    }
  };

  const getBorderColor = () => {
    switch (action.status) {
      case 'PENDING':
      case 'IN_PROGRESS':
        return 'border-blue-400 bg-blue-50/50';
      case 'FAILED':
        return 'border-red-400 bg-red-50/50';
      case 'COMPLETED':
        return 'border-green-400 bg-green-50/50';
      default:
        return 'border-slate-200';
    }
  };

  return (
    <div
      className={`border-l-2 pl-3 py-1 transition-colors ${getBorderColor()}`}
    >
      <div className="flex items-baseline justify-between gap-2 -mb-1">
        <div className="flex items-baseline gap-2">
          <span className="text-xs font-semibold text-slate-900">You</span>
          <span className="text-xs text-slate-400">Just now</span>
        </div>
        {getStatusIndicator()}
      </div>
      <div className="text-sm text-slate-700 leading-tight mt-0">
        <MarkdownViewer content={commentText} />
      </div>
      {action.status === 'FAILED' && action.error_message && (
        <div className="mt-1 text-xs text-red-600">{action.error_message}</div>
      )}
    </div>
  );
};

export const ExternalTaskDetail: React.FC<ExternalTaskDetailProps> = ({
  externalTaskId,
  onClose,
}) => {
  const [showAllComments, setShowAllComments] = React.useState(false);
  const [isLinkingPanelOpen, setIsLinkingPanelOpen] = React.useState(false);
  const [selectedTaskId, setSelectedTaskId] = React.useState<string | null>(
    null,
  );
  const [newComment, setNewComment] = React.useState('');
  const [retryingActionId, setRetryingActionId] = React.useState<string | null>(
    null,
  );
  const [dismissingActionId, setDismissingActionId] = React.useState<
    string | null
  >(null);

  const {
    data: externalTask,
    isLoading,
    error,
  } = externalTaskApi.useExternalTaskRetrieveQuery(
    { id: externalTaskId },
    {
      // Enable polling when there are pending actions
      pollingInterval: getPollingInterval(undefined), // Initial value
    },
  );

  // Dynamic polling based on pending actions
  const pollingInterval = getPollingInterval(externalTask);
  React.useEffect(() => {
    // This effect doesn't do anything directly - polling is handled by RTK Query
    // We just need to re-render when polling interval changes
  }, [pollingInterval]);

  // Re-fetch the query with updated polling interval
  // The second query hook with dynamic polling interval ensures we poll when actions are pending
  externalTaskApi.useExternalTaskRetrieveQuery(
    { id: externalTaskId },
    {
      pollingInterval,
      skip: false,
    },
  );

  // Fetch supported actions for this external task
  const { data: supportedActionsData } =
    externalTaskApi.useExternalTaskSupportedActionsQuery(
      { id: externalTaskId },
      { skip: !externalTaskId },
    );

  // Check which actions are supported
  const supportedActions = supportedActionsData?.supported_action_types ?? [];
  const supportsStatusChange = supportedActions.includes('CHANGE_STATUS');
  const supportsDueDateChange = supportedActions.includes('CHANGE_DUE_DATE');
  const supportsAddComment = supportedActions.includes('ADD_COMMENT');

  const [linkTask, { isLoading: isLinking }] =
    externalTaskApi.useExternalTaskLinkCreateMutation();
  const [unlinkTask, { isLoading: isUnlinking }] =
    externalTaskApi.useExternalTaskLinkDestroyMutation();
  const [addComment, { isLoading: isAddingComment }] =
    externalTaskApi.useExternalTaskAddCommentMutation();
  const [changeStatus, { isLoading: isChangingStatus }] =
    externalTaskApi.useExternalTaskChangeStatusMutation();
  const [changeDueDate, { isLoading: isChangingDueDate }] =
    externalTaskApi.useExternalTaskChangeDueDateMutation();
  const [retryAction] = externalTaskApi.useExternalTaskRetryActionMutation();
  const [dismissAction] =
    externalTaskApi.useExternalTaskDismissActionMutation();

  // Floating UI setup for task linking panel
  const { refs, floatingStyles, context } = useFloating({
    open: isLinkingPanelOpen,
    onOpenChange: setIsLinkingPanelOpen,
    placement: 'left',
    middleware: [offset(32), shift({ padding: 10 })],
    whileElementsMounted: autoUpdate,
  });

  const click = useClick(context);
  const dismiss = useDismiss(context);
  const { getReferenceProps, getFloatingProps } = useInteractions([
    click,
    dismiss,
  ]);

  const handleLinkTasks = async (taskIds: string[]) => {
    try {
      await Promise.all(
        taskIds.map((taskId) =>
          linkTask({
            externalTaskPk: externalTaskId,
            body: { internal_task_id: taskId },
          }).unwrap(),
        ),
      );
      // Keep panel open so user can link more tasks if needed
    } catch (err) {
      // Error is handled by RTK Query and shown in UI
    }
  };

  const handleUnlinkTask = async (linkId: string) => {
    try {
      await unlinkTask({
        externalTaskPk: externalTaskId,
        id: linkId,
        pk: linkId,
      }).unwrap();
    } catch (err) {
      // Error is handled by RTK Query and shown in UI
    }
  };

  const handleAddComment = async () => {
    if (!newComment.trim()) return;

    try {
      await addComment({
        id: externalTaskId,
        addCommentPayload: { comment: newComment },
      }).unwrap();
      setNewComment('');
    } catch (err) {
      // Error is handled by RTK Query and shown in UI
    }
  };

  const handleStatusChange = async (
    newStatus: 'PENDING' | 'IN_PROGRESS' | 'COMPLETED',
  ) => {
    try {
      await changeStatus({
        id: externalTaskId,
        changeStatusPayload: { status: newStatus },
      }).unwrap();
    } catch (err) {
      // Error is handled by RTK Query and shown in UI
    }
  };

  const handleDueDateChange = async (newDueDate: string) => {
    try {
      await changeDueDate({
        id: externalTaskId,
        changeDueDatePayload: { due_date: newDueDate },
      }).unwrap();
    } catch (err) {
      // Error is handled by RTK Query and shown in UI
    }
  };

  const handleRetryAction = async (actionId: string) => {
    setRetryingActionId(actionId);
    try {
      await retryAction({
        id: externalTaskId,
        actionId,
      }).unwrap();
    } catch (err) {
      // Error is handled by RTK Query
    } finally {
      setRetryingActionId(null);
    }
  };

  const handleDismissAction = async (actionId: string) => {
    setDismissingActionId(actionId);
    try {
      await dismissAction({
        id: externalTaskId,
        actionId,
      }).unwrap();
    } catch (err) {
      // Error is handled by RTK Query
    } finally {
      setDismissingActionId(null);
    }
  };

  if (isLoading) {
    return (
      <Dialog
        open
        onOpenChange={(open) => {
          if (!open) {
            onClose();
          }
        }}
        modal={false}
      >
        <RightDialogContent
          className="bg-white h-[95vh] overflow-hidden flex flex-col"
          zIndex={2}
        >
          <DialogTitle className="sr-only">External Task Details</DialogTitle>
          <div className="flex justify-center items-center py-12">
            <div className="animate-pulse space-y-6 w-full max-w-md px-4">
              <div className="h-6 bg-slate-200 rounded w-3/4" />
              <div className="h-4 bg-slate-200 rounded w-1/2" />
              <div className="space-y-3">
                <div className="h-4 bg-slate-200 rounded" />
                <div className="h-4 bg-slate-200 rounded w-5/6" />
                <div className="h-4 bg-slate-200 rounded w-4/6" />
              </div>
            </div>
          </div>
        </RightDialogContent>
      </Dialog>
    );
  }

  if (error || !externalTask) {
    return (
      <Dialog
        open
        onOpenChange={(open) => {
          if (!open) {
            onClose();
          }
        }}
        modal={false}
      >
        <RightDialogContent
          className="bg-white h-[95vh] overflow-hidden flex flex-col"
          zIndex={2}
        >
          <DialogTitle className="sr-only">External Task Details</DialogTitle>
          <div className="flex justify-center items-center h-full">
            <div className="text-center space-y-2">
              <h3 className="text-lg font-semibold text-red-600">Error</h3>
              <p className="text-sm text-slate-500">
                Failed to load external task details. Please try again.
              </p>
            </div>
          </div>
        </RightDialogContent>
      </Dialog>
    );
  }

  const formatTimeAgo = (dateString: string | null) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffMinutes = Math.floor(diffMs / (1000 * 60));

    if (diffDays > 0) return `${diffDays}d ago`;
    if (diffHours > 0) return `${diffHours}h ago`;
    if (diffMinutes > 0) return `${diffMinutes}m ago`;
    return 'Just now';
  };

  /**
   * Convert backend ISO date string to local date string for DatePicker.
   * The DatePicker expects a string and uses new Date(string), which treats
   * date-only strings as UTC midnight. We need to convert to local timezone format.
   */
  const parseDateForPicker = (
    dateString: string | null | undefined,
  ): string | null => {
    if (!dateString) return null;
    // Extract date part (handles both "2025-11-12" and "2025-11-12T00:00:00Z")
    const [datePart] = dateString.split('T');
    // Return as local datetime string (without Z) so new Date() treats it as local time
    return `${datePart}T00:00:00`;
  };

  const handleTaskClick = (taskId: string) => {
    setSelectedTaskId((prevId) => (prevId === taskId ? null : taskId));
  };

  const handleDialogOpenChange = (open: boolean) => {
    // Prevent closing the dialog if the linking panel is open
    if (!open && isLinkingPanelOpen) {
      return;
    }

    if (!open) {
      onClose();
    }
  };

  // Filter pending actions to show (exclude completed ones older than 5 seconds)
  const visiblePendingActions = (externalTask.pending_actions || []).filter(
    (action) => {
      if (action.status !== 'COMPLETED') return true;
      // Show completed actions for 5 seconds after completion
      if (action.completed_at) {
        const completedAt = new Date(action.completed_at).getTime();
        const now = Date.now();
        return now - completedAt < 5000;
      }
      return true;
    },
  );

  // Separate comment actions from other actions (comments shown inline)
  const pendingCommentActions = visiblePendingActions.filter(
    (a) => a.action_type === 'ADD_COMMENT',
  );
  const nonCommentPendingActions = visiblePendingActions.filter(
    (a) => a.action_type !== 'ADD_COMMENT',
  );

  // Further separate failed actions from pending/in-progress
  const failedActions = nonCommentPendingActions.filter(
    (a) => a.status === 'FAILED',
  );
  const inProgressActions = nonCommentPendingActions.filter(
    (a) => a.status === 'PENDING' || a.status === 'IN_PROGRESS',
  );
  const failedCommentActions = pendingCommentActions.filter(
    (a) => a.status === 'FAILED',
  );
  const inProgressCommentActions = pendingCommentActions.filter(
    (a) => a.status === 'PENDING' || a.status === 'IN_PROGRESS',
  );

  // Check if there's an action in progress for status/due date (for visual feedback)
  const hasStatusActionPending = visiblePendingActions.some(
    (a) =>
      a.action_type === 'CHANGE_STATUS' &&
      (a.status === 'PENDING' || a.status === 'IN_PROGRESS'),
  );
  const hasDueDateActionPending = visiblePendingActions.some(
    (a) =>
      a.action_type === 'CHANGE_DUE_DATE' &&
      (a.status === 'PENDING' || a.status === 'IN_PROGRESS'),
  );

  const renderPendingActionsSection = () => {
    // Only show non-comment actions that are pending/in-progress (comments shown inline)
    if (inProgressActions.length === 0) return null;

    return (
      <TaskSection title="Pending Actions" className="px-4" compact>
        <div className="space-y-2">
          {inProgressActions.map((action) => (
            <PendingActionItem
              key={action.id}
              action={action}
              onRetry={handleRetryAction}
              onDismiss={handleDismissAction}
              isRetrying={retryingActionId === action.id}
              isDismissing={dismissingActionId === action.id}
            />
          ))}
        </div>
      </TaskSection>
    );
  };

  const renderFailedActionsSection = () => {
    // Show all failed actions (including comments) in their own section
    const allFailedActions = [...failedActions, ...failedCommentActions];
    if (allFailedActions.length === 0) return null;

    return (
      <TaskSection title="Failed Actions" className="px-4" compact>
        <div className="space-y-2">
          {allFailedActions.map((action) => (
            <PendingActionItem
              key={action.id}
              action={action}
              onRetry={handleRetryAction}
              onDismiss={handleDismissAction}
              isRetrying={retryingActionId === action.id}
              isDismissing={dismissingActionId === action.id}
            />
          ))}
        </div>
      </TaskSection>
    );
  };

  return (
    <Dialog open onOpenChange={handleDialogOpenChange} modal={false}>
      <RightDialogContent
        className={`bg-white h-[95vh] overflow-hidden flex flex-col ${
          selectedTaskId ? 'max-w-[75vw] w-[75vw]' : ''
        }`}
        zIndex={2}
      >
        <DialogTitle className="sr-only">External Task Details</DialogTitle>

        {/* Header Section */}
        <div className="px-4 pt-4 pb-3 border-b border-slate-100">
          <div className="space-y-2">
            <h2 className="text-xl font-semibold text-slate-900">
              {externalTask.name}
            </h2>
            <div className="flex items-center gap-2 flex-wrap">
              <Select
                value={externalTask.status}
                onValueChange={(value) =>
                  handleStatusChange(value as 'PENDING' | 'COMPLETED')
                }
                disabled={
                  !supportsStatusChange ||
                  isChangingStatus ||
                  hasStatusActionPending
                }
              >
                <SelectTrigger
                  className={`w-auto h-auto px-2 py-1 border-0 bg-transparent hover:bg-slate-100 focus:ring-0 focus:ring-offset-0 transition-all  rounded-md ${
                    isChangingStatus || hasStatusActionPending
                      ? 'animate-pulse shadow-[0_0_8px_rgba(59,130,246,0.5)]'
                      : ''
                  } ${!supportsStatusChange ? 'cursor-not-allowed opacity-60' : ''}`}
                >
                  <div className="flex items-center gap-1.5">
                    {hasStatusActionPending ? (
                      <Loader2 className="w-2 h-2 animate-spin text-blue-500" />
                    ) : (
                      <div
                        className={`w-2 h-2 rounded-full ${
                          externalTask.status === 'COMPLETED'
                            ? 'bg-green-500'
                            : 'bg-yellow-500'
                        }`}
                      />
                    )}
                    <SelectValue />
                  </div>
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="PENDING">Pending</SelectItem>
                  <SelectItem value="COMPLETED">Completed</SelectItem>
                </SelectContent>
              </Select>
              {externalTask.needs_update && (
                <Badge color="orange">Needs Update</Badge>
              )}
              <span className="text-slate-400">•</span>
              <span className="text-sm text-slate-600 capitalize">
                {externalTask.source.toLowerCase()}
              </span>
              <span className="text-slate-400">•</span>
              <FirmLink
                entityId={externalTask.firm_id}
                className="text-sm text-slate-600 font-normal"
              >
                {externalTask.firm_name}
              </FirmLink>
              {externalTask.matter_name && externalTask.matter_id && (
                <>
                  <span className="text-slate-400">•</span>
                  <MatterLink
                    entityId={externalTask.matter_id}
                    className="text-sm text-slate-600 font-normal"
                  >
                    {externalTask.matter_name}
                  </MatterLink>
                </>
              )}
              {externalTask.external_url && (
                <>
                  <span className="text-slate-400">•</span>
                  <a
                    href={externalTask.external_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 text-sm text-blue-600 hover:text-blue-700 hover:underline transition-colors"
                  >
                    <ExternalLink className="h-3.5 w-3.5" />
                    <span>
                      View in{' '}
                      {externalTask.source.charAt(0).toUpperCase() +
                        externalTask.source.slice(1).toLowerCase()}
                    </span>
                  </a>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Main content area */}
        <div className="flex-1 overflow-y-scroll no-scrollbar">
          {selectedTaskId ? (
            /* Split view when a task is selected */
            <div className="h-full flex">
              {/* Task Detail - Left side (50% width) */}
              <div className="w-1/2 overflow-y-auto border-r border-slate-200">
                <TaskDetail
                  taskId={selectedTaskId}
                  matterId={externalTask.matter_id as string}
                  onClose={() => setSelectedTaskId(null)}
                  isModal={false}
                />
              </div>

              {/* External Task Detail - Right side (50% width) */}
              <div className="w-1/2 overflow-y-auto">
                <div className="space-y-0 divide-y divide-slate-150">
                  {/* Failed Actions Section */}
                  {renderFailedActionsSection()}

                  {/* Pending Actions Section */}
                  {renderPendingActionsSection()}

                  {/* Description Section */}
                  {externalTask.description && (
                    <TaskSection title="Description" className="px-4" compact>
                      <div className="text-sm text-slate-700">
                        <MarkdownViewer content={externalTask.description} />
                      </div>
                    </TaskSection>
                  )}

                  {/* Task Details Section */}
                  <TaskSection
                    title={`${externalTask.source} Details`}
                    className="px-4"
                    compact
                  >
                    <div className="grid grid-cols-[repeat(auto-fit,minmax(140px,1fr))] gap-x-4 gap-y-2 text-sm">
                      <div>
                        <div className="text-xs text-slate-500 mb-1">
                          Due Date
                        </div>
                        <div
                          className={`transition-all rounded-md ${
                            isChangingDueDate || hasDueDateActionPending
                              ? 'animate-pulse shadow-[0_0_8px_rgba(59,130,246,0.5)]'
                              : ''
                          }`}
                        >
                          <DatePicker
                            date={parseDateForPicker(externalTask.due_date)}
                            onChange={(date) => {
                              if (date) {
                                handleDueDateChange(
                                  date.toISOString().split('T')[0],
                                );
                              }
                            }}
                            placeholder={
                              supportsDueDateChange
                                ? 'Set due date'
                                : 'Not supported'
                            }
                            disabled={
                              !supportsDueDateChange ||
                              isChangingDueDate ||
                              hasDueDateActionPending
                            }
                            className={`font-medium text-slate-900 ${!supportsDueDateChange ? 'opacity-60' : ''}`}
                          />
                        </div>
                      </div>
                      <div>
                        <div className="text-xs text-slate-500">
                          Last Synced
                        </div>
                        <div className="font-medium">
                          {formatTimeAgo(externalTask.last_synced_at)}
                        </div>
                      </div>
                      {externalTask.assignee && (
                        <div>
                          <div className="text-xs text-slate-500">Assignee</div>
                          <div className="font-medium">
                            {externalTask.assignee.name}
                          </div>
                        </div>
                      )}
                      {externalTask.creator && (
                        <div>
                          <div className="text-xs text-slate-500">Creator</div>
                          <div className="font-medium">
                            {externalTask.creator.name}
                          </div>
                        </div>
                      )}
                      {externalTask.external_created_at && (
                        <div>
                          <div className="text-xs text-slate-500">
                            Created (External)
                          </div>
                          <div className="font-medium">
                            {formatDate(externalTask.external_created_at)}
                          </div>
                        </div>
                      )}
                    </div>
                  </TaskSection>

                  {/* Task Linking Section */}
                  <TaskSection title="Link Tasks" className="px-4" compact>
                    <div className="space-y-3">
                      {/* Button to open linking panel */}
                      <div ref={refs.setReference} {...getReferenceProps()}>
                        <Button
                          onClick={() =>
                            setIsLinkingPanelOpen(!isLinkingPanelOpen)
                          }
                          outline
                          className="w-full"
                        >
                          <Link2 className="h-4 w-4 mr-2" />
                          {isLinkingPanelOpen
                            ? 'Close Task Linking'
                            : 'Link Internal Tasks'}
                        </Button>
                      </div>

                      {/* Currently linked tasks */}
                      <div className="space-y-2">
                        <h4 className="text-xs font-semibold text-slate-700 uppercase tracking-wide">
                          Currently Linked Tasks (
                          {externalTask.linked_tasks.length})
                        </h4>
                        <LinkedTasksList
                          linkedTasks={externalTask.linked_tasks}
                          externalTaskId={externalTaskId}
                          onUnlink={handleUnlinkTask}
                          isUnlinking={isUnlinking}
                          onTaskClick={handleTaskClick}
                          selectedTaskId={selectedTaskId}
                        />
                      </div>
                    </div>
                  </TaskSection>

                  {/* Rule Matches Section */}
                  {(externalTask.rule_evaluations?.length ||
                    externalTask.is_excluded ||
                    externalTask.is_exclusion_pending) && (
                    <TaskSection title="Rule Matches" className="px-4" compact>
                      <ExternalTaskMatchStatus
                        evaluations={externalTask.rule_evaluations || []}
                        isExcluded={externalTask.is_excluded || false}
                        excludedByRuleName={externalTask.excluded_by_rule_name}
                        excludedByRuleId={externalTask.excluded_by_rule_id}
                        isExclusionPending={
                          externalTask.is_exclusion_pending || false
                        }
                        pendingExclusionRuleName={
                          externalTask.pending_exclusion_rule_name
                        }
                        pendingExclusionRuleId={
                          externalTask.pending_exclusion_rule_id
                        }
                        firmId={externalTask.firm_id}
                      />
                    </TaskSection>
                  )}

                  {/* Add Comment Section */}
                  {supportsAddComment && (
                    <TaskSection title="Add Comment" className="px-4" compact>
                      <div className="space-y-3">
                        <Textarea
                          value={newComment}
                          onChange={(e) => setNewComment(e.target.value)}
                          placeholder="Enter your comment..."
                          rows={4}
                          disabled={isAddingComment}
                          className="w-full resize-none border-slate-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-100 transition-all"
                        />
                        <Button
                          onClick={handleAddComment}
                          disabled={!newComment.trim() || isAddingComment}
                          className="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-2.5 rounded-lg transition-colors disabled:bg-slate-300 disabled:cursor-not-allowed"
                        >
                          {isAddingComment
                            ? 'Adding Comment...'
                            : 'Add Comment'}
                        </Button>
                      </div>
                    </TaskSection>
                  )}

                  {/* Replies/Comments Section */}
                  {(externalTask.replies.length > 0 ||
                    inProgressCommentActions.length > 0) &&
                    (() => {
                      const sortedReplies = [...externalTask.replies].sort(
                        (a, b) =>
                          new Date(b.created_at).getTime() -
                          new Date(a.created_at).getTime(),
                      );
                      const visibleReplies = showAllComments
                        ? sortedReplies
                        : sortedReplies.slice(0, 3);
                      const hiddenCount = sortedReplies.length - 3;
                      const totalComments =
                        externalTask.replies.length +
                        inProgressCommentActions.length;

                      return (
                        <TaskSection
                          title={`${externalTask.source} Comments (${totalComments})`}
                          className="px-4"
                          compact
                        >
                          <div className="space-y-2">
                            {/* In-progress comments shown first */}
                            {inProgressCommentActions.map((action) => (
                              <PendingCommentItem
                                key={action.id}
                                action={action}
                                onRetry={handleRetryAction}
                                onDismiss={handleDismissAction}
                                isRetrying={retryingActionId === action.id}
                                isDismissing={dismissingActionId === action.id}
                              />
                            ))}

                            {/* Existing replies */}
                            {visibleReplies.map((reply) => (
                              <div
                                key={reply.id}
                                className="group border-l-2 border-slate-200 pl-3 py-1 hover:border-blue-400 transition-colors"
                              >
                                <div className="flex items-baseline gap-2 -mb-1">
                                  <span className="text-xs font-semibold text-slate-900">
                                    {reply.creator?.name || 'Unknown'}
                                  </span>
                                  <span className="text-xs text-slate-400">
                                    {formatTimeAgo(reply.external_created_at)}
                                  </span>
                                </div>
                                <div className="text-sm text-slate-700 leading-tight mt-0">
                                  <MarkdownViewer content={reply.content} />
                                </div>
                              </div>
                            ))}

                            {hiddenCount > 0 && !showAllComments && (
                              <button
                                type="button"
                                onClick={() => setShowAllComments(true)}
                                className="text-xs text-blue-600 hover:text-blue-700 font-medium pl-3 py-1"
                              >
                                Show {hiddenCount} more{' '}
                                {hiddenCount === 1 ? 'comment' : 'comments'}
                              </button>
                            )}

                            {showAllComments && hiddenCount > 0 && (
                              <button
                                type="button"
                                onClick={() => setShowAllComments(false)}
                                className="text-xs text-slate-500 hover:text-slate-700 font-medium pl-3 py-1"
                              >
                                Show less
                              </button>
                            )}
                          </div>
                        </TaskSection>
                      );
                    })()}
                </div>
              </div>
            </div>
          ) : (
            /* Normal view when no task is selected */
            <div className="space-y-0 divide-y divide-slate-150">
              {/* Failed Actions Section */}
              {renderFailedActionsSection()}

              {/* Pending Actions Section */}
              {renderPendingActionsSection()}

              {/* Description Section */}
              {externalTask.description && (
                <TaskSection title="Description" className="px-4" compact>
                  <div className="text-sm text-slate-700">
                    <MarkdownViewer content={externalTask.description} />
                  </div>
                </TaskSection>
              )}

              {/* Task Details Section */}
              <TaskSection
                title={`${externalTask.source} Details`}
                className="px-4"
                compact
              >
                <div className="grid grid-cols-[repeat(auto-fit,minmax(140px,1fr))] gap-x-4 gap-y-2 text-sm">
                  <div>
                    <div className="text-xs text-slate-500 mb-1">Due Date</div>
                    <div
                      className={`transition-all rounded-md ${
                        isChangingDueDate || hasDueDateActionPending
                          ? 'animate-pulse shadow-[0_0_8px_rgba(59,130,246,0.5)]'
                          : ''
                      }`}
                    >
                      <DatePicker
                        date={parseDateForPicker(externalTask.due_date)}
                        onChange={(date) => {
                          if (date) {
                            handleDueDateChange(
                              date.toISOString().split('T')[0],
                            );
                          }
                        }}
                        placeholder={
                          supportsDueDateChange
                            ? 'Set due date'
                            : 'Not supported'
                        }
                        disabled={
                          !supportsDueDateChange ||
                          isChangingDueDate ||
                          hasDueDateActionPending
                        }
                        className={`font-medium text-slate-900 ${!supportsDueDateChange ? 'opacity-60' : ''}`}
                      />
                    </div>
                  </div>
                  <div>
                    <div className="text-xs text-slate-500">Last Synced</div>
                    <div className="font-medium">
                      {formatTimeAgo(externalTask.last_synced_at)}
                    </div>
                  </div>
                  {externalTask.assignee && (
                    <div>
                      <div className="text-xs text-slate-500">Assignee</div>
                      <div className="font-medium">
                        {externalTask.assignee.name}
                      </div>
                    </div>
                  )}
                  {externalTask.creator && (
                    <div>
                      <div className="text-xs text-slate-500">Creator</div>
                      <div className="font-medium">
                        {externalTask.creator.name}
                      </div>
                    </div>
                  )}
                  {externalTask.external_created_at && (
                    <div>
                      <div className="text-xs text-slate-500">
                        Created (External)
                      </div>
                      <div className="font-medium">
                        {formatDate(externalTask.external_created_at)}
                      </div>
                    </div>
                  )}
                </div>
              </TaskSection>

              {/* Task Linking Section */}
              <TaskSection title="Link Tasks" className="px-4" compact>
                <div className="space-y-3">
                  {/* Button to open linking panel */}
                  <div ref={refs.setReference} {...getReferenceProps()}>
                    <Button
                      onClick={() => setIsLinkingPanelOpen(!isLinkingPanelOpen)}
                      outline
                      className="w-full"
                    >
                      <Link2 className="h-4 w-4 mr-2" />
                      {isLinkingPanelOpen
                        ? 'Close Task Linking'
                        : 'Link Internal Tasks'}
                    </Button>
                  </div>

                  {/* Currently linked tasks */}
                  <div className="space-y-2">
                    <h4 className="text-xs font-semibold text-slate-700 uppercase tracking-wide">
                      Currently Linked Tasks ({externalTask.linked_tasks.length}
                      )
                    </h4>
                    <LinkedTasksList
                      linkedTasks={externalTask.linked_tasks}
                      externalTaskId={externalTaskId}
                      onUnlink={handleUnlinkTask}
                      isUnlinking={isUnlinking}
                      onTaskClick={handleTaskClick}
                      selectedTaskId={selectedTaskId}
                    />
                  </div>
                </div>
              </TaskSection>

              {/* Rule Matches Section */}
              {(externalTask.rule_evaluations?.length ||
                externalTask.is_excluded ||
                externalTask.is_exclusion_pending) && (
                <TaskSection title="Rule Matches" className="px-4" compact>
                  <ExternalTaskMatchStatus
                    evaluations={externalTask.rule_evaluations || []}
                    isExcluded={externalTask.is_excluded || false}
                    excludedByRuleName={externalTask.excluded_by_rule_name}
                    excludedByRuleId={externalTask.excluded_by_rule_id}
                    isExclusionPending={
                      externalTask.is_exclusion_pending || false
                    }
                    pendingExclusionRuleName={
                      externalTask.pending_exclusion_rule_name
                    }
                    pendingExclusionRuleId={
                      externalTask.pending_exclusion_rule_id
                    }
                    firmId={externalTask.firm_id}
                  />
                </TaskSection>
              )}

              {/* Add Comment Section */}
              {supportsAddComment && (
                <TaskSection title="Add Comment" className="px-4" compact>
                  <div className="space-y-3">
                    <Textarea
                      value={newComment}
                      onChange={(e) => setNewComment(e.target.value)}
                      placeholder="Enter your comment..."
                      rows={4}
                      disabled={isAddingComment}
                      className="w-full resize-none border-slate-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-100 transition-all"
                    />
                    <Button
                      onClick={handleAddComment}
                      disabled={!newComment.trim() || isAddingComment}
                      className="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-2.5 rounded-lg transition-colors disabled:bg-slate-300 disabled:cursor-not-allowed"
                    >
                      {isAddingComment ? 'Adding Comment...' : 'Add Comment'}
                    </Button>
                  </div>
                </TaskSection>
              )}

              {/* Replies/Comments Section */}
              {(externalTask.replies.length > 0 ||
                inProgressCommentActions.length > 0) &&
                (() => {
                  const sortedReplies = [...externalTask.replies].sort(
                    (a, b) =>
                      new Date(b.created_at).getTime() -
                      new Date(a.created_at).getTime(),
                  );
                  const visibleReplies = showAllComments
                    ? sortedReplies
                    : sortedReplies.slice(0, 3);
                  const hiddenCount = sortedReplies.length - 3;
                  const totalComments =
                    externalTask.replies.length +
                    inProgressCommentActions.length;

                  return (
                    <TaskSection
                      title={`${externalTask.source} Comments (${totalComments})`}
                      className="px-4"
                      compact
                    >
                      <div className="space-y-2">
                        {/* In-progress comments shown first */}
                        {inProgressCommentActions.map((action) => (
                          <PendingCommentItem
                            key={action.id}
                            action={action}
                            onRetry={handleRetryAction}
                            onDismiss={handleDismissAction}
                            isRetrying={retryingActionId === action.id}
                            isDismissing={dismissingActionId === action.id}
                          />
                        ))}

                        {/* Existing replies */}
                        {visibleReplies.map((reply) => (
                          <div
                            key={reply.id}
                            className="group border-l-2 border-slate-200 pl-3 py-1 hover:border-blue-400 transition-colors"
                          >
                            <div className="flex items-baseline gap-2 -mb-1">
                              <span className="text-xs font-semibold text-slate-900">
                                {reply.creator?.name || 'Unknown'}
                              </span>
                              <span className="text-xs text-slate-400">
                                {formatTimeAgo(reply.external_created_at)}
                              </span>
                            </div>
                            <div className="text-sm text-slate-700 leading-tight mt-0">
                              <MarkdownViewer content={reply.content} />
                            </div>
                          </div>
                        ))}

                        {hiddenCount > 0 && !showAllComments && (
                          <button
                            type="button"
                            onClick={() => setShowAllComments(true)}
                            className="text-xs text-blue-600 hover:text-blue-700 font-medium pl-3 py-1"
                          >
                            Show {hiddenCount} more{' '}
                            {hiddenCount === 1 ? 'comment' : 'comments'}
                          </button>
                        )}

                        {showAllComments && hiddenCount > 0 && (
                          <button
                            type="button"
                            onClick={() => setShowAllComments(false)}
                            className="text-xs text-slate-500 hover:text-slate-700 font-medium pl-3 py-1"
                          >
                            Show less
                          </button>
                        )}
                      </div>
                    </TaskSection>
                  );
                })()}
            </div>
          )}
        </div>
      </RightDialogContent>

      {/* Floating task linking panel - rendered via portal */}
      {isLinkingPanelOpen &&
        createPortal(
          <div
            ref={refs.setFloating}
            style={floatingStyles}
            {...getFloatingProps()}
            role="dialog"
            aria-label="Link Internal Tasks"
            className="z-[9999] w-[480px] bg-white border border-slate-300 rounded-lg shadow-xl overflow-hidden"
          >
            <div className="flex items-center justify-between px-4 py-3 bg-slate-50 border-b border-slate-200">
              <h3 className="font-semibold text-sm text-slate-900">
                Link Internal Tasks
              </h3>
              <button
                type="button"
                onClick={() => setIsLinkingPanelOpen(false)}
                className="p-1 text-slate-500 hover:text-slate-700 hover:bg-slate-200 rounded transition-colors"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
            <div className="p-4 overflow-y-auto max-h-[636px]">
              <TaskLinkingInterface
                matterId={externalTask.matter_id}
                linkedTaskIds={externalTask.linked_tasks.map((t) => t.id)}
                onLink={handleLinkTasks}
                isLinking={isLinking}
              />
            </div>
          </div>,
          document.body,
        )}
    </Dialog>
  );
};
